import { CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ChatChannel, ChatUserResult } from '@/api/chat';
import { Loader2, MessageSquare, Users, X, Pin, GripVertical } from 'lucide-react';
import { useState, useEffect } from 'react';
import { PinnedChannelItem } from './PinnedChannelItem';
import { PendingSessionChannelItem } from './PendingSessionChannelItem';
import { usePinnedChannels } from '@/hooks/usePinnedChannels';
import { useFeatures, Feature } from '@/contexts/FeaturesContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Utility function to format time ago
const formatTimeAgo = (dateString: string): string => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  
  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
  
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return `${weeks}w`;
};

interface ChatSidebarProps {
  channels: ChatChannel[];
  channelsLoading: boolean;
  selectedChannelId?: string;
  onSelectChannel: (channelId: string) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  searchResults?: ChatUserResult[];
  searching: boolean;
  pendingGroupUsers: ChatUserResult[];
  onAddUserToGroup: (user: ChatUserResult) => void;
  onRemoveUserFromGroup: (userId: string) => void;
}

export function ChatSidebar({
  channels,
  channelsLoading,
  selectedChannelId,
  onSelectChannel,
  searchTerm,
  onSearchTermChange,
  searchResults,
  searching,
  pendingGroupUsers,
  onAddUserToGroup,
  onRemoveUserFromGroup,
}: ChatSidebarProps) {
  // Force re-render every minute to update "time ago" display
  const [, setCurrentTime] = useState(Date.now());
  
  // Pinned channels with cross-component sync
  const { pinnedChannels, pinnedOrder, togglePin, reorderPins } = usePinnedChannels();

  const [draggedChannelId, setDraggedChannelId] = useState<string | null>(null);
  const { isEnabled } = useFeatures();
  const showProfilePictures = isEnabled(Feature.PROFILE_PICTURE.name);
  const [dragOverChannelId, setDragOverChannelId] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  const handleTogglePin = (channelId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    togglePin(channelId);
  };

  const handleDragStart = (e: React.DragEvent, channelId: string) => {
    console.log('[Drag] Start:', channelId);
    setDraggedChannelId(channelId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', channelId);
  };

  const handleDragOver = (e: React.DragEvent, channelId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedChannelId && draggedChannelId !== channelId && pinnedChannels.has(channelId)) {
      console.log('[Drag] Over:', channelId, 'dragging:', draggedChannelId);
      setDragOverChannelId(channelId);
    }
  };

  const handleDrop = (e: React.DragEvent, targetChannelId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const draggedId = draggedChannelId || e.dataTransfer.getData('text/plain');
    console.log('[Drag] Drop on:', targetChannelId, 'draggedId:', draggedId);
    console.log('[Drag] pinnedChannels:', [...pinnedChannels]);
    console.log('[Drag] pinnedOrder before:', pinnedOrder);
    
    if (!draggedId || draggedId === targetChannelId || !pinnedChannels.has(targetChannelId)) {
      console.log('[Drag] Drop rejected - draggedId:', draggedId, 'targetChannelId:', targetChannelId, 'targetIsPinned:', pinnedChannels.has(targetChannelId));
      setDraggedChannelId(null);
      setDragOverChannelId(null);
      return;
    }

    // Reorder pinned channels using the shared hook
    reorderPins(draggedId, targetChannelId);

    setDraggedChannelId(null);
    setDragOverChannelId(null);
  };

  const handleDragEnd = () => {
    setDraggedChannelId(null);
    setDragOverChannelId(null);
  };

  const hasPendingGroup = pendingGroupUsers.length > 0;

  // Separate channels into categories
  // Channels with pending sessions show in Pending section
  // Channels with confirmed sessions (but no pending) show in Upcoming section
  // Channels with BOTH show in BOTH sections
  const pendingSessionChannels = channels.filter(ch => ch.hasPendingSessions);
  const upcomingSessionChannels = channels.filter(ch => ch.hasConfirmedSessions || ch.isPinned);
  const regularChannels = channels.filter(ch => !ch.isPinned && !ch.hasConfirmedSessions && !ch.hasPendingSessions);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && searchTerm === '' && pendingGroupUsers.length > 0) {
      // Remove the last tag when backspace is pressed with empty input
      onRemoveUserFromGroup(pendingGroupUsers[pendingGroupUsers.length - 1].id);
    }
  };

  return (
    <>

      <div className="px-4 pb-3 space-y-2 flex-shrink-0 pt-3">
        <div className="relative min-h-10 flex items-center flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          {pendingGroupUsers.map((user) => (
            <Badge key={user.id} variant="secondary" className="gap-1 flex-shrink-0">
              {user.fullName}
              <button
                onClick={() => onRemoveUserFromGroup(user.id)}
                className="hover:bg-muted rounded-full"
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <input
            type="text"
            className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground"
            placeholder={hasPendingGroup ? "Search more users..." : "Search users or groups"}
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {searching && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />
          )}
        </div>
        {searchTerm.trim().length >= 2 && (
          <div className="border rounded-md max-h-44 overflow-y-auto divide-y">
            {searchResults && searchResults.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground">No users found</div>
            )}
            {searchResults && searchResults.map((user) => {
              const isAlreadySelected = pendingGroupUsers.some(u => u.id === user.id);
              if (isAlreadySelected) return null;
              
              return (
                <button
                  key={user.id}
                  className="w-full text-left px-3 py-2 hover:bg-muted"
                  onClick={() => onAddUserToGroup(user)}
                >
                  <div className="font-medium">{user.fullName}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                </button>
              );
            })}
            {searchResults && searchResults.length > 0 && searchResults.every(u => pendingGroupUsers.some(p => p.id === u.id)) && (
              <div className="p-3 text-sm text-muted-foreground">All matching users already added</div>
            )}
          </div>
        )}
      </div>
      <Separator className="flex-shrink-0" />
      <CardContent className="p-0 flex-1 overflow-y-auto min-h-0">
        {channelsLoading && (
          <div className="p-4 text-sm text-muted-foreground">Loading chats...</div>
        )}
        {!channelsLoading && channels.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">No chats yet. A chat opens with your tutor once you book a session.</div>
        )}
        
        {/* Pending Sessions Section - Highest Priority */}
        {!channelsLoading && pendingSessionChannels.length > 0 && (
          <div className="mb-2">
            <div className="px-4 py-2 text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
              <span>⏳</span>
              <span>Pending Sessions</span>
            </div>
            <div className="px-2 space-y-1">
              {pendingSessionChannels.map((ch) => (
                <PendingSessionChannelItem
                  key={ch.id}
                  channel={ch}
                  isSelected={selectedChannelId === ch.id}
                  onClick={() => onSelectChannel(ch.id)}
                />
              ))}
            </div>
            {(upcomingSessionChannels.length > 0 || regularChannels.length > 0) && (
              <div className="h-px bg-border my-2 mx-4" />
            )}
          </div>
        )}
        
        {/* Pinned Session Chats Section */}
        {!channelsLoading && upcomingSessionChannels.length > 0 && (
          <div className="mb-2">
            <div className="px-4 py-2 text-xs font-semibold text-burgundy-700 uppercase tracking-wide flex items-center gap-1.5">
              <span>📌</span>
              <span>Upcoming Sessions</span>
            </div>
            <div className="px-2 space-y-1">
              {upcomingSessionChannels.map((ch) => (
                <PinnedChannelItem
                  key={ch.id}
                  channel={ch}
                  isSelected={selectedChannelId === ch.id}
                  onClick={() => onSelectChannel(ch.id)}
                />
              ))}
            </div>
            {regularChannels.length > 0 && (
              <div className="h-px bg-border my-2 mx-4" />
            )}
          </div>
        )}
        
        {/* Regular Channels Section */}
        <div className="divide-y">
          {regularChannels.map((ch) => {
            const isPinnedByUser = pinnedChannels.has(ch.id);
            const isDragging = draggedChannelId === ch.id;
            const isDragOver = dragOverChannelId === ch.id;
            
            return (
              <div
                key={ch.id}
                draggable={isPinnedByUser}
                onDragStart={(e) => handleDragStart(e, ch.id)}
                onDragOver={(e) => handleDragOver(e, ch.id)}
                onDrop={(e) => handleDrop(e, ch.id)}
                onDragEnd={handleDragEnd}
                onDragLeave={() => setDragOverChannelId(null)}
                onClick={() => onSelectChannel(ch.id)}
                className={`w-full text-left px-4 py-3 hover:bg-muted transition-colors group cursor-pointer ${
                  selectedChannelId === ch.id 
                    ? 'bg-muted' 
                    : ch.hasUnread
                      ? 'bg-rose-50 dark:bg-rose-950/30 border-l-2 border-rose-400' 
                      : ''
                } ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'border-t-2 border-primary' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isPinnedByUser && (
                      <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0 cursor-grab active:cursor-grabbing" />
                    )}
                    {ch.type === 'GROUP' ? (
                      <Users className="h-4 w-4 flex-shrink-0" />
                    ) : showProfilePictures && ch.directMessageAvatarUrl ? (
                      <Avatar className="h-6 w-6 flex-shrink-0">
                        <AvatarImage src={`/api/v1/files/${ch.directMessageAvatarUrl}`} alt={ch.name || 'Direct message'} />
                        <AvatarFallback>
                          {(ch.name || 'D').split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <MessageSquare className="h-4 w-4 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {ch.name || (ch.type === 'GROUP' ? 'Group' : 'Direct')}
                      </div>
                      {ch.lastMessageContent && (
                        <div className="text-xs text-muted-foreground truncate">
                          {ch.lastMessageSenderName ? `${ch.lastMessageSenderName}: ` : ''}{ch.lastMessageContent}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => handleTogglePin(ch.id, e)}
                      className={`p-1 rounded hover:bg-muted-foreground/10 transition-opacity ${
                        isPinnedByUser ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}
                      title={isPinnedByUser ? 'Unpin chat' : 'Pin chat'}
                    >
                      <Pin className={`h-3.5 w-3.5 ${
                        isPinnedByUser ? 'text-rose-600 fill-rose-600' : 'text-muted-foreground'
                      }`} />
                    </button>
                    {ch.hasUnread && (
                      <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                    )}
                    <span className={`text-xs whitespace-nowrap ${
                      ch.hasUnread ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-muted-foreground'
                    }`}>
                      {formatTimeAgo(ch.lastMessageAt || ch.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </>
  );
}
